/* ============================================================
   Art Sizer — widget script (theme asset for Shopify)
   Drop in: themes/your-theme/assets/art-sizer.js
   Boots automatically on DOMContentLoaded against every .art-sizer
   on the page; also exposes window.ArtSizer for manual control.
   ============================================================ */
(function(){
/* =========================================================
     ART SIZER — vanilla JS, single function, no dependencies.
     The same code runs in the demo and in Shopify.

     Coordinate system: 1 SVG unit = 1 inch.
     World height: 84" (just over a 5'10" person).
     World width:  flexible — extends with the artwork.

     Public API:
       ArtSizer.render(rootEl, { widthIn, heightIn, label, humanHeightIn, hangCenterIn })
     Or set data-width-in / data-height-in / data-label on the root and call init().
  ========================================================= */

  const ArtSizer = (() => {
    // Silhouette: inline SVG path in a 60x175 native coordinate space.
    // Represents a standing person 70 inches (5'10") tall.
    // Using an inline path avoids PNG CORS issues and enables brand-color control.
    const SIL_VB_W = 60;
    const SIL_VB_H = 175;
    const SIL_PATH = [
      'M30 2 C25 2 22 6 22 11 C22 16 25 20 30 20 C35 20 38 16 38 11 C38 6 35 2 30 2 Z',
      'M22 22 L38 22 L40 30 L44 45 L43 50 L40 50 L37 40 L37 85',
      'L39 85 L44 170 L40 170 L34 100 L31 100 L31 170 L29 170 L29 100',
      'L26 100 L20 170 L16 170 L21 85 L23 85 L23 40 L20 50 L17 50 L16 45 L20 30 Z',
    ].join(' ');

    // Defaults
    const DEFAULTS = {
      humanHeightIn: 70,        // 5'10"
      hangCenterIn: 57,         // gallery standard center
      worldHeightIn: 84,        // total visual ceiling — gives ~14" headroom above person
      personX: 18,              // x-position of silhouette center (in)
      gapIn: 12,                // breathing room between silhouette and artwork (in)
      labelGapIn: 2,            // gap between artwork right edge and dimension label
      paddingRightIn: 4,
    };

    function fmt(n) {
      const r = Math.round(n * 100) / 100;
      return Number.isInteger(r) ? String(r) : r.toFixed(2);
    }

    // Common print sizes — fallback ladder when dimensions are missing.
    const COMMON_SIZES = [
      { widthIn: 10, heightIn: 13 },
      { widthIn: 13, heightIn: 10 },
      { widthIn: 13, heightIn: 18 },
      { widthIn: 18, heightIn: 13 },
      { widthIn: 18, heightIn: 24 },
      { widthIn: 24, heightIn: 18 },
    ];
    const FALLBACK_SIZE = { widthIn: 18, heightIn: 24 };

    function pickFallbackForAspect(aspect) {
      let best = FALLBACK_SIZE;
      let bestErr = Infinity;
      for (const s of COMMON_SIZES) {
        const err = Math.abs((s.widthIn / s.heightIn) - aspect);
        if (err < bestErr) { bestErr = err; best = s; }
      }
      return best;
    }

    function resolveDimensions(root, opts) {
      const wRaw = opts.widthIn  ?? root.dataset.widthIn;
      const hRaw = opts.heightIn ?? root.dataset.heightIn;
      const w = wRaw === '' || wRaw == null ? null : +wRaw;
      const h = hRaw === '' || hRaw == null ? null : +hRaw;
      if (w && h && !isNaN(w) && !isNaN(h)) return { widthIn: w, heightIn: h, source: 'explicit' };

      const cachedAspect = root._artImgAspect;
      if (cachedAspect) {
        if (w && !h)  return { widthIn: w, heightIn: w / cachedAspect, source: 'aspect-derived' };
        if (h && !w)  return { widthIn: h * cachedAspect, heightIn: h, source: 'aspect-derived' };
      }

      if (cachedAspect) {
        const m = pickFallbackForAspect(cachedAspect);
        return { ...m, source: 'common-size-fallback' };
      }

      return { ...FALLBACK_SIZE, source: 'default-fallback' };
    }

    function render(root, opts = {}) {
      const cfg = Object.assign({}, DEFAULTS, opts);

      const imgUrl = opts.imageUrl ?? root.dataset.imageUrl;
      if (imgUrl && !root._artImgAspect && !root._artImgLoading) {
        root._artImgLoading = true;
        const probe = new Image();
        probe.crossOrigin = 'anonymous';
        probe.onload = () => {
          root._artImgAspect = probe.naturalWidth / probe.naturalHeight;
          root._artImgLoading = false;
          render(root, opts);
        };
        probe.onerror = () => { root._artImgLoading = false; };
        probe.src = imgUrl;
      }

      const dims = resolveDimensions(root, opts);
      const widthIn = dims.widthIn;
      const heightIn = dims.heightIn;
      root.dataset.dimensionSource = dims.source;

      const explicitLabel = opts.label ?? root.dataset.label;
      const isFallback = dims.source === 'common-size-fallback' || dims.source === 'default-fallback';
      const label = explicitLabel
        ? explicitLabel
        : `${fmt(widthIn)} x ${fmt(heightIn)} in${isFallback ? ' · est.' : ''}`;

      // Person geometry — silhouette path native viewBox SIL_VB_W x SIL_VB_H
      const personHeightIn = cfg.humanHeightIn;
      const personWidthIn  = (SIL_VB_W / SIL_VB_H) * personHeightIn;

      const worldH = cfg.worldHeightIn;
      const floorY = worldH;
      const personTopY = floorY - personHeightIn;
      const personLeftX = cfg.personX - personWidthIn / 2;

      const artCenterY = floorY - cfg.hangCenterIn;
      const artTopY    = artCenterY - heightIn / 2;
      const artLeftX   = cfg.personX + personWidthIn / 2 + cfg.gapIn;

      const worldW = artLeftX + widthIn + cfg.labelGapIn + 30 + cfg.paddingRightIn;

      // Build SVG
      const svgNS = 'http://www.w3.org/2000/svg';
      const svg = document.createElementNS(svgNS, 'svg');
      svg.setAttribute('viewBox', `0 0 ${worldW} ${worldH}`);
      svg.setAttribute('preserveAspectRatio', 'xMidYMax meet');
      svg.setAttribute('role', 'img');
      svg.setAttribute('aria-label',
        `Artwork ${fmt(widthIn)} by ${fmt(heightIn)} inches shown at scale next to a ${Math.round(personHeightIn/12)} foot ${Math.round(personHeightIn%12)} inch person.`);

      // Baseline floor
      const baseline = document.createElementNS(svgNS, 'line');
      baseline.setAttribute('x1', 0);
      baseline.setAttribute('x2', worldW);
      baseline.setAttribute('y1', floorY);
      baseline.setAttribute('y2', floorY);
      baseline.setAttribute('class', 'art-sizer__baseline');
      svg.appendChild(baseline);

      // Silhouette — inline SVG path scaled into position (brand navy, no CORS)
      const silScaleX = personWidthIn / SIL_VB_W;
      const silScaleY = personHeightIn / SIL_VB_H;
      const silG = document.createElementNS(svgNS, 'g');
      silG.setAttribute('class', 'art-sizer__silhouette');
      silG.setAttribute('transform',
        `translate(${personLeftX},${personTopY}) scale(${silScaleX},${silScaleY})`);
      const silPath = document.createElementNS(svgNS, 'path');
      silPath.setAttribute('d', SIL_PATH);
      silG.appendChild(silPath);
      svg.appendChild(silG);

      // Artwork — image or flat fill
      let art;
      if (imgUrl) {
        art = document.createElementNS(svgNS, 'image');
        art.setAttribute('href', imgUrl);
        art.setAttribute('preserveAspectRatio', 'none');
      } else {
        art = document.createElementNS(svgNS, 'rect');
        art.setAttribute('class', 'art-sizer__artwork');
      }
      art.setAttribute('x', artLeftX);
      art.setAttribute('y', artTopY);
      art.setAttribute('width', widthIn);
      art.setAttribute('height', heightIn);
      svg.appendChild(art);

      // Subtle brand-colored outline so light artworks don't vanish
      if (imgUrl) {
        const outline = document.createElementNS(svgNS, 'rect');
        outline.setAttribute('x', artLeftX);
        outline.setAttribute('y', artTopY);
        outline.setAttribute('width', widthIn);
        outline.setAttribute('height', heightIn);
        outline.setAttribute('fill', 'none');
        outline.setAttribute('stroke', 'rgba(26,58,107,0.18)');
        outline.setAttribute('stroke-width', '0.08');
        svg.appendChild(outline);
      }

      // Dimension label to the right of artwork
      const labelText = document.createElementNS(svgNS, 'text');
      labelText.setAttribute('x', artLeftX + widthIn + cfg.labelGapIn);
      labelText.setAttribute('y', artCenterY);
      labelText.setAttribute('dominant-baseline', 'middle');
      labelText.setAttribute('class', 'art-sizer__art-label');
      labelText.textContent = label;
      svg.appendChild(labelText);

      // Mount
      const stage = root.querySelector('.art-sizer__stage');
      stage.replaceChildren(svg);

      // Update caption below
      const caption = root.querySelector('.art-sizer__caption');
      if (caption) {
        const dimNum = caption.querySelector('.dim-num');
        if (dimNum) {
          dimNum.textContent = label;
        } else {
          const span = document.createElement('span');
          span.className = 'dim-num';
          span.textContent = label;
          caption.replaceChildren(span);
        }
      }
    }

    function init(selector = '.art-sizer') {
      document.querySelectorAll(selector).forEach(el => render(el));
    }

    // Re-render when a <details> containing an art-sizer is toggled open.
    function hookDetails() {
      document.querySelectorAll('details').forEach(det => {
        det.addEventListener('toggle', function onToggle() {
          if (!det.open) return;
          det.querySelectorAll('.art-sizer').forEach(el => render(el));
          det.removeEventListener('toggle', onToggle);
        });
      });
    }

    return { render, init, hookDetails };
  })();

  window.ArtSizer = ArtSizer;
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => { ArtSizer.init(); ArtSizer.hookDetails(); });
  } else {
    ArtSizer.init();
    ArtSizer.hookDetails();
  }
})();
